import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  customerListOrders,
  customerLogout,
  type CustomerOrderResponse,
  type ErrorResponse,
} from '@vn-propane/shared';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguageSelector } from '@/components/ui/language-selector';
import { Loader2, Package, ChevronRight, LogOut, Plus, CircleHelp } from 'lucide-react';

type OrderData = CustomerOrderResponse;

export default function OrdersPage() {
  const { t, i18n } = useTranslation(['orders', 'common', 'errors', 'auth']);
  const navigate = useNavigate();

  const [orders, setOrders] = useState<OrderData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadOrders() {
      try {
        const result = await customerListOrders();
        if (result.data?.data) {
          setOrders(result.data.data);
        } else if (result.error) {
          const errorResponse = result.error as ErrorResponse;
          if (errorResponse.error?.code === '401') {
            navigate('/login');
            return;
          }
          setError(errorResponse.error?.message || t('errors:failedToLoadOrders'));
        }
      } catch {
        setError(t('errors:unexpectedError'));
      } finally {
        setIsLoading(false);
      }
    }

    loadOrders();
  }, [t, navigate]);

  const handleLogout = async () => {
    try {
      await customerLogout();
    } finally {
      navigate('/login');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-carbon-200 text-carbon-800';
      case 'assigned':
      case 'dispatched':
        return 'bg-info/10 text-info';
      case 'in_progress':
        return 'bg-warning text-carbon-950';
      case 'arrived':
        return 'bg-propane-100 text-propane-700';
      case 'delivered':
      case 'completed':
        return 'bg-success/10 text-success';
      case 'cancelled':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-carbon-200 text-carbon-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return t('status.pending');
      case 'assigned':
        return t('status.assigned');
      case 'dispatched':
        return t('status.dispatched');
      case 'in_progress':
        return t('status.in_progress');
      case 'arrived':
        return t('status.arrived');
      case 'delivered':
        return t('status.delivered');
      case 'completed':
        return t('status.completed');
      case 'cancelled':
        return t('status.cancelled');
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="mt-2 text-muted-foreground">{t('common:loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40 relative">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <a
          href={i18n.language === 'en' ? `${import.meta.env.VITE_HELP_URL || '/help'}/en/customer/` : `${import.meta.env.VITE_HELP_URL || '/help'}/customer/`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title={t('common:navigation.help')}
        >
          <CircleHelp className="h-5 w-5" />
        </a>
        <LanguageSelector />
        <Button variant="outline" size="sm" onClick={handleLogout}>
          <LogOut className="h-4 w-4 mr-1" />
          {t('auth:logout')}
        </Button>
      </div>

      <div className="max-w-2xl mx-auto p-4 pt-16">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/home" className="shrink-0" aria-label="Home">
              <img src={`${import.meta.env.BASE_URL}logo-mark-dark.png`} alt="" className="h-10 w-10" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-extrabold tracking-tight truncate">{t('myOrders')}</h1>
              <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                giao<span className="text-propane-500">gas</span>
              </p>
            </div>
          </div>
          <Button onClick={() => navigate('/orders/new')} className="shrink-0">
            <Plus className="h-4 w-4 mr-1" />
            {t('newOrder')}
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {orders.length === 0 ? (
          <div className="bg-background rounded-sm border shadow-sm p-8 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t('noOrders')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <Link
                key={order.id}
                to={`/orders/${order.id}`}
                className="block bg-background rounded-sm border shadow-sm p-4 hover:border-primary transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest ${getStatusColor(order.status)}`}
                      >
                        {getStatusLabel(order.status)}
                      </span>
                      <span className="text-sm text-muted-foreground tabular-nums">
                        {formatDate(order.created_at)}
                      </span>
                    </div>
                    <p className="font-medium">
                      {order.items.length} {t('items')}
                    </p>
                    <p className="text-lg font-extrabold tabular-nums text-primary">
                      {formatCurrency(order.total_amount)}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
